import nativeSpec from "./native-spec.json" with { type: "json" };
import appsSpec from "./apps-spec.json" with { type: "json" };
import filesSpec from "./files-spec.json" with { type: "json" };
import { announceActionUrl, findActionUrl } from "./browser-links";
import { apiBaseUrl, validateApiKey } from "../auth/client";
import {
	getActiveProfileForRequest,
	getValidAccessToken,
} from "../auth/profiles";
import { WHOP_SCOPES } from "../auth/scopes";
import type { Profile } from "../auth/types";
import { requiredOverrides } from "./required-overrides";
import { API_VERSION_DATE, whopApiFetch } from "../lib/http";
import {
	ACCOUNT_FIELDS,
	constrainPathParams,
	filterSpecByTag,
} from "./filter-spec";
import { matchesPathTemplate } from "./path-template";

// A path param holds exactly one URL segment; constrain the baked specs so
// incur's generated zod schemas reject slash-bearing argv values before they
// are interpolated into request paths (see constrainPathParams).
constrainPathParams(nativeSpec as Record<string, unknown>);
constrainPathParams(appsSpec as Record<string, unknown>);
constrainPathParams(filesSpec as Record<string, unknown>);

export { nativeSpec, appsSpec, filesSpec };
export { ACCOUNT_FIELDS, filterSpecByTag };

/**
 * The API version the built CLI is pinned to. sync-spec.ts bakes it into
 * native-spec.json as info["x-api-version-date"] at build time (and fails the
 * build when it's missing); without the header the backend resolves requests
 * to the legacy base version, not the latest shapes the spec commands are
 * generated from.
 */
export { API_VERSION_DATE };

interface SpecOperation {
	parameters?: Array<{ name?: string; in?: string }>;
	requestBody?: {
		content?: Record<
			string,
			{
				schema?: {
					properties?: Record<string, { type?: unknown }>;
				};
			}
		>;
	};
}

const CLI_UNAVAILABLE_SCOPES = new Set(["developer:manage_api_key"]);

/** Finds the spec operation for a concrete request path + method (templated segments are wildcards). */
function findOperation(method: string, pathname: string): SpecOperation | null {
	const m = method.toLowerCase();
	for (const spec of [nativeSpec, appsSpec, filesSpec]) {
		const paths = (spec as { paths?: Record<string, Record<string, unknown>> })
			.paths;
		if (!paths) continue;
		for (const [template, ops] of Object.entries(paths)) {
			if (matchesPathTemplate(template, pathname) && ops[m]) {
				return ops[m] as SpecOperation;
			}
		}
	}
	return null;
}

function declaresQueryParam(op: SpecOperation, name: string): boolean {
	return (op.parameters ?? []).some((p) => p.name === name && p.in === "query");
}

function declaresBodyProp(op: SpecOperation, name: string): boolean {
	const props =
		op.requestBody?.content?.["application/json"]?.schema?.properties;
	return !!props && Object.prototype.hasOwnProperty.call(props, name);
}

function nullableObjectBodyProps(op: SpecOperation): string[] {
	const props =
		op.requestBody?.content?.["application/json"]?.schema?.properties;
	if (!props) return [];

	return Object.entries(props).flatMap(([name, property]) => {
		const types = Array.isArray(property.type) ? property.type : [];
		return types.includes("object") && types.includes("null") ? [name] : [];
	});
}

/**
 * The Whop API nests its error message under `{ error: { message } }`, but incur
 * only reads a top-level `message`, so every failure collapses to "HTTP 400".
 * Lift the real message to the top level so spec commands report what's wrong.
 */
function extractApiMessage(payload: unknown): string | undefined {
	if (payload == null || typeof payload !== "object") return undefined;
	const p = payload as Record<string, unknown>;
	const err = p.error;
	if (
		err &&
		typeof err === "object" &&
		typeof (err as Record<string, unknown>).message === "string"
	) {
		return (err as Record<string, string>).message;
	}
	for (const value of [p.message, p.detail, err]) {
		if (typeof value === "string") return value;
	}
	return undefined;
}

let envAccount: {
	apiKey: string;
	accountId: Promise<string | undefined>;
} | null = null;

function normalizeApiKey(key: string): string {
	return key.replace(/^Bearer\s+/i, "").trim();
}

async function resolveAccountId(
	profile: Profile | null,
	apiKey: string,
): Promise<string | undefined> {
	if (profile?.accountId) return profile.accountId;

	const envKey = process.env.WHOP_API_KEY
		? normalizeApiKey(process.env.WHOP_API_KEY)
		: undefined;
	if (!profile && envKey && apiKey === envKey) {
		// Memoize so concurrent requests with the same key share one validation,
		// but never cache a miss: a transient failure or not-yet-valid key must be
		// re-checked on the next request rather than locking the process to "no
		// account". A rejection is swallowed to undefined so it stays non-fatal —
		// API commands then surface a clear error instead of the proxy throwing.
		if (envAccount?.apiKey !== envKey) {
			envAccount = {
				apiKey: envKey,
				accountId: validateApiKey(envKey, apiBaseUrl())
					.then((result) =>
						result.status === "valid" || result.status === "underscoped"
							? result.identity.id
							: undefined,
					)
					.catch(() => undefined),
			};
		}
		const pending = envAccount;
		const resolved = await pending.accountId;
		if (resolved === undefined && envAccount === pending) envAccount = null;
		return resolved;
	}

	return undefined;
}

/**
 * Resolves the active account id for hand-written commands, using the same rules
 * as the fetch proxy: WHOP_API_KEY's account when set, otherwise the active
 * profile's account. Lets commands that build their own requests reach the
 * account without a saved login profile.
 */
export async function resolveActiveAccountId(): Promise<string | undefined> {
	const profile = getActiveProfileForRequest();
	const rawKey = profile
		? await getValidAccessToken(profile)
		: (process.env.WHOP_API_KEY ?? null);
	if (!rawKey) return undefined;
	return resolveAccountId(profile, normalizeApiKey(rawKey));
}

/**
 * Returns a fetch handler that proxies requests to the Whop API. It:
 *  - injects WHOP_API_KEY when set, otherwise the active profile's credential,
 *  - pins every request to the baked API version via Api-Version-Date,
 *  - auto-fills the active account (company_id/account_id) on any operation that
 *    declares it but didn't receive it — user-scoped OAuth tokens require this,
 *  - surfaces the API's real error message instead of a bare "HTTP <status>".
 *
 * Passed to incur's `{ fetch, openapi }` command to drive spec subcommands.
 */
export function createWhopFetch(): (request: Request) => Promise<Response> {
	return async (request: Request): Promise<Response> => {
		const profile = getActiveProfileForRequest();
		const rawKey = profile
			? await getValidAccessToken(profile)
			: (process.env.WHOP_API_KEY ?? null);

		if (!rawKey) {
			const message = profile
				? "Stored credential not found. Run `whop login` again."
				: "Not logged in. Run `whop login` first.";
			return Response.json({ message }, { status: 401 });
		}

		const token = normalizeApiKey(rawKey);

		const incomingUrl = new URL(request.url);
		const op = findOperation(request.method, incomingUrl.pathname);
		const accountId = await resolveAccountId(profile, token);
		const method = request.method.toUpperCase();

		// Path-templated account scoping. Endpoints like /accounts/{account_id}/...
		// carry the account in the URL path. transformParams re-homes those params to
		// optional query flags (incur can't express an optional path param), so here we
		// resolve the value — an explicit --account_id/--company_id wins over the active
		// account — back into the `{name}` segment and drop the helper query param so it
		// never leaks downstream. `new URL` percent-encodes the braces to `%7B..%7D`.
		for (const name of ACCOUNT_FIELDS) {
			const encoded = `%7B${name}%7D`;
			const rawTemplate = `{${name}}`;
			if (
				!incomingUrl.pathname.includes(encoded) &&
				!incomingUrl.pathname.includes(rawTemplate)
			) {
				continue;
			}
			const override = incomingUrl.searchParams.get(name);
			incomingUrl.searchParams.delete(name);
			const value = override ?? accountId;
			if (!value) {
				return Response.json(
					{
						message:
							"No account selected. Pass --account_id (or --company_id), set WHOP_API_KEY, or run `whop quickstart` to choose a business.",
					},
					{ status: 400 },
				);
			}
			incomingUrl.pathname = incomingUrl.pathname
				.replaceAll(encoded, encodeURIComponent(value))
				.replaceAll(rawTemplate, encodeURIComponent(value));
		}

		let body = request.body ? await request.text() : undefined;
		if (op && body) {
			const parsed = safeParse(body);
			if (parsed && typeof parsed === "object") {
				const record = parsed as Record<string, unknown>;
				let changed = false;
				for (const name of nullableObjectBodyProps(op)) {
					if (record[name] === "null") {
						record[name] = null;
						changed = true;
					}
				}
				if (changed) body = JSON.stringify(record);
			}
		}

		// The single place account scoping happens: auto-fill the active account on
		// any operation that declares it but didn't receive it. Never overwrites a
		// value the caller set.
		if (op && accountId) {
			if (method === "GET" || method === "DELETE" || method === "HEAD") {
				for (const name of ACCOUNT_FIELDS) {
					if (
						!incomingUrl.searchParams.has(name) &&
						declaresQueryParam(op, name)
					) {
						incomingUrl.searchParams.set(name, accountId);
					}
				}
			} else {
				const parsed = body ? safeParse(body) : {};
				if (parsed && typeof parsed === "object") {
					const rec = parsed as Record<string, unknown>;
					let changed = false;
					for (const name of ACCOUNT_FIELDS) {
						// The API infers company_id from a plan and rejects it when one is
						// given, so never inject company_id alongside a plan_id.
						if (name === "company_id" && rec.plan_id != null) continue;
						if (rec[name] == null && declaresBodyProp(op, name)) {
							rec[name] = accountId;
							changed = true;
						}
					}
					if (changed) body = JSON.stringify(parsed);
				}
			}
		}

		const overrides = requiredOverrides(
			method,
			incomingUrl.pathname,
			profile?.method === "oauth" ? "user" : "business",
		);
		if (overrides.length > 0) {
			const parsed = body ? safeParse(body) : {};
			const rec =
				parsed && typeof parsed === "object"
					? (parsed as Record<string, unknown>)
					: {};
			const violation = overrides.find(
				({ name }) => rec[name] == null || rec[name] === "",
			);
			if (violation) {
				return Response.json({ message: violation.error }, { status: 400 });
			}
		}

		const base = apiBaseUrl().replace(/\/$/, "");
		const target = `${base}${incomingUrl.pathname}${incomingUrl.search}`;

		const headers = new Headers(request.headers);
		headers.set("Content-Type", "application/json");

		const res = await whopApiFetch(target, token, {
			method: request.method,
			headers,
			body,
		});
		if (res.ok) {
			// Some flows (social account OAuth, hosted KYC, hosted deposits) can
			// only be finished in a browser — surface that instead of silently
			// printing a URL in the payload. Only on mutations: list/get responses
			// echo the same fields without asking for anything.
			if (method === "POST" || method === "PATCH") {
				const text = await res.text();
				const actionUrl = findActionUrl(safeParse(text));
				if (actionUrl) await announceActionUrl(actionUrl);
				return new Response(text, { status: res.status, headers: res.headers });
			}
			return res;
		}

		// Lift the API's nested error message to the top level for incur.
		const text = await res.text();
		const payload = safeParse(text);
		const errorActionUrl = findActionUrl(
			(payload as Record<string, unknown> | null)?.error,
		);
		if (errorActionUrl) await announceActionUrl(errorActionUrl);
		let message = extractApiMessage(payload) ?? text ?? `HTTP ${res.status}`;
		if (res.status === 403) {
			const scope = /not authorized for the (\S+) scope/i.exec(message)?.[1];
			if (scope) {
				if (CLI_UNAVAILABLE_SCOPES.has(scope)) {
					message = appendSentence(
						message,
						"API key management requires a first-party dashboard session and is not available through the CLI. Manage keys at https://whop.com/dashboard.",
					);
				} else {
					// A scope we request at login is grantable — the stored token just
					// predates it. A scope we don't request has no OAuth path at all.
					message += (WHOP_SCOPES as readonly string[]).includes(scope)
						? " Your login predates this scope — run `whop login` to re-authorize."
						: " This resource requires an API-key login (`whop login --api-key`).";
				}
			}
		}
		message = withJourneyHint(message, payload);
		return Response.json(
			{ message, error: payload ?? text },
			{ status: res.status },
		);
	};
}

/**
 * Known dead-ends in multi-step journeys (ads launch, media generation) where
 * the API's message says what's wrong but not which CLI command fixes it.
 * Matched on the lifted message so the hints hold across endpoints that share
 * backend validations.
 */
const JOURNEY_HINTS: Array<{ pattern: RegExp; hint: string }> = [
	{
		pattern: /exactly one of ad_group_id or ad_group/i,
		hint: 'Pass --ad_group_id for an existing ad group, or create the whole tree in one call: whop ads create --title "..." --creatives \'[{"id":"file_x"}]\' --ad_group \'{"title":"...","conversion_location":"website","ad_campaign":{"title":"...","platform":"meta","objective":"sales","status":"draft","budget_amount":25,"budget_optimization":"ad_campaign"}}\'',
	},
	{
		pattern: /connect an ads payment method/i,
		hint: 'Configure billing with `whop accounts update-preferences --account_id <account_id> --ads_payment_methods \'{"primary":{"type":"platform_balance"}}\'`, funding the balance first via `whop deposits create` if needed.',
	},
	{
		pattern: /payment method requires user authentication/i,
		hint: "Run `whop login` (browser OAuth) — API-key credentials can only reorder already-configured payment methods.",
	},
];

function withJourneyHint(message: string, payload: unknown): string {
	const depositUrl =
		payload && typeof payload === "object"
			? ((payload as { error?: { deposit_url?: string } }).error?.deposit_url ??
				undefined)
			: undefined;
	if (depositUrl) return appendSentence(message, `Deposit at ${depositUrl}`);

	const hint = JOURNEY_HINTS.find(({ pattern }) => pattern.test(message))?.hint;
	return hint ? appendSentence(message, hint) : message;
}

function appendSentence(message: string, addition: string): string {
	const trimmed = message.trim();
	return `${trimmed}${/[.!?]$/.test(trimmed) ? "" : "."} ${addition}`;
}

function safeParse(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}
